import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { injectIntl, FormattedMessage } from 'react-intl';

import messages from './messagesSleeperSupplements';
import SleeperSupplementsView from './SleeperSupplementsView';
import SleeperSupplementActions from './SleeperSupplementActions';
import inlineStyles from './styles';
import {
	groupSelectionsBySegment,
	shouldAutoUncheckConnectingDoors,
	matchesPassengerAndSegment,
	isSameSupplement,
} from './SleeperSupplementUtils';

const StyledDialog = styled(Dialog)(() => ({
	'& .MuiPaper-root': {
		...inlineStyles.dialogPaper,
	},
}));

const StyledDialogContent = styled(DialogContent)(inlineStyles.dialogContent);
const StyledDialogTitle = styled(DialogTitle)(inlineStyles.dialogTitle);

const SleeperSupplementsDialog = ({
	open,
	faresSelected,
	sleeperSupplements,
	passengerNumber,
	onDiscardSleeperSupplements,
	selectedSleeperSupplements,
	onChangeSleeperSupplements,
}) => {
	const [tempSelectedSleeperSupplements, setTempSelectedSleeperSupplements] =
		useState([]);
	const [requestConnectingDoorsByLeg, setRequestConnectingDoorsByLeg] =
		useState({});

	useEffect(() => {
		if (open) {
			setTempSelectedSleeperSupplements(selectedSleeperSupplements || []);
			// Initialize per-leg connecting doors state
			const connectingDoorsByLeg = {};
			selectedSleeperSupplements?.forEach((supplement) => {
				if (supplement.seatPreferences?.includes('CONNECTING')) {
					supplement.linkedTravelSegments?.forEach((segmentId) => {
						connectingDoorsByLeg[segmentId] = true;
					});
				}
			});
			setRequestConnectingDoorsByLeg(connectingDoorsByLeg);
		}
	}, [open, selectedSleeperSupplements]);

	// Auto-uncheck connecting doors if conditions are no longer valid
	useEffect(() => {
		const selectionsBySegment = groupSelectionsBySegment(
			tempSelectedSleeperSupplements,
		);
		const updatedConnectingDoors = { ...requestConnectingDoorsByLeg };
		let hasChanges = false;

		Object.keys(requestConnectingDoorsByLeg).forEach((segmentId) => {
			if (requestConnectingDoorsByLeg[segmentId]) {
				const selections = selectionsBySegment[segmentId] || [];

				if (shouldAutoUncheckConnectingDoors(passengerNumber, selections)) {
					updatedConnectingDoors[segmentId] = false;
					hasChanges = true;
				}
			}
		});

		if (hasChanges) {
			setRequestConnectingDoorsByLeg(updatedConnectingDoors);
		}
	}, [tempSelectedSleeperSupplements, passengerNumber]);

	const handleCheckSleeperSupplement = (
		selectedSleeperSupplementInSegment,
		isChecked,
		travelSegmentId,
	) => {
		if (isChecked) {
			// Remove any existing selection for this passenger and travel segment
			const filteredSupplements = tempSelectedSleeperSupplements.filter(
				(supplement) =>
					!matchesPassengerAndSegment(
						supplement,
						selectedSleeperSupplementInSegment,
						travelSegmentId,
					),
			);

			const newSelection = {
				...selectedSleeperSupplementInSegment,
				selected: true,
			};

			setTempSelectedSleeperSupplements([...filteredSupplements, newSelection]);
		} else {
			// Remove selection if it matches passenger, segment, and supplement ID
			const updatedSupplements = tempSelectedSleeperSupplements.filter(
				(supplement) => {
					const matchesPaxAndSeg = matchesPassengerAndSegment(
						supplement,
						selectedSleeperSupplementInSegment,
						travelSegmentId,
					);
					const matchesId = isSameSupplement(
						supplement,
						selectedSleeperSupplementInSegment,
					);
					return !(matchesPaxAndSeg && matchesId);
				},
			);
			setTempSelectedSleeperSupplements(updatedSupplements);
		}
	};

	const handleDiscardSleeperSupplements = () => {
		setTempSelectedSleeperSupplements(selectedSleeperSupplements || []);
		setRequestConnectingDoorsByLeg({});
		onDiscardSleeperSupplements();
	};

	const handleSaveSleeperSupplements = () => {
		// Add seatPreferences to supplements based on per-leg checkbox state
		const supplementsWithPreferences = tempSelectedSleeperSupplements.map(
			(supplement) => {
				const hasConnectingDoorsRequest = supplement.linkedTravelSegments?.some(
					(segmentId) => requestConnectingDoorsByLeg[segmentId],
				);

				return {
					...supplement,
					seatPreferences: hasConnectingDoorsRequest ? ['CONNECTING'] : [],
				};
			},
		);
		onChangeSleeperSupplements(supplementsWithPreferences);
	};

	return (
		<StyledDialog
			open={open}
			onClose={handleDiscardSleeperSupplements}
			maxWidth="md"
			fullWidth
		>
			<StyledDialogTitle>
				<FormattedMessage {...messages.dialogTitle} />
			</StyledDialogTitle>
			<StyledDialogContent>
				<SleeperSupplementsView
					faresSelected={faresSelected}
					sleeperSupplements={sleeperSupplements}
					passengerNumber={passengerNumber}
					onCheckSleeperSupplement={handleCheckSleeperSupplement}
					selectedSleeperSupplements={tempSelectedSleeperSupplements}
					requestConnectingDoorsByLeg={requestConnectingDoorsByLeg}
					setRequestConnectingDoorsByLeg={setRequestConnectingDoorsByLeg}
				/>
			</StyledDialogContent>
			<SleeperSupplementActions
				onDiscardSleeperSupplements={handleDiscardSleeperSupplements}
				onChangeSleeperSupplements={handleSaveSleeperSupplements}
				tempSelectedSupplements={tempSelectedSleeperSupplements}
			/>
		</StyledDialog>
	);
};

SleeperSupplementsDialog.propTypes = {
	intl: PropTypes.object,
	open: PropTypes.bool.isRequired,
	faresSelected: PropTypes.object.isRequired,
	sleeperSupplements: PropTypes.array.isRequired,
	passengerNumber: PropTypes.number.isRequired,
	onDiscardSleeperSupplements: PropTypes.func.isRequired,
	selectedSleeperSupplements: PropTypes.array,
	onChangeSleeperSupplements: PropTypes.func.isRequired,
};

SleeperSupplementsDialog.defaultProps = {
	selectedSleeperSupplements: [],
};

export { SleeperSupplementsDialog as SleeperSupplementsDialogAlias };

export default injectIntl(SleeperSupplementsDialog);
